/**
 * Safely extracts a human-readable error message from a Fetch API `Response`
 * without consuming the response body more than once.
 *
 * Parsing strategy:
 * - If the `Content-Type` header indicates JSON, the body is parsed as JSON
 *   and `message` or `error` fields are returned when present.
 * - For non-JSON responses, the body is read as plain text.
 * - In test or mock environments where headers may be missing but `json()`
 *   exists, JSON parsing is attempted once. If parsing succeeds but no
 *   message is found, the function returns `null` and does not fall back
 *   to reading text, ensuring the body is never read twice.
 *
 * All parsing errors are swallowed, and `null` is returned when no meaningful
 * error message can be extracted.
 *
 * @param response - A Fetch API `Response` object or a compatible mock.
 * @returns A resolved error message string, or `null` if unavailable.
 */
export async function getErrorMessage(
  response: Response
): Promise<string | null> {
  const resp = response as any;

  const tryJson = async (): Promise<{ ok: boolean; msg: string | null }> => {
    if (typeof resp.json !== 'function') {
      return { ok: false, msg: null };
    }

    try {
      const data = await resp.json();
      return {
        ok: true,
        msg: data?.message ?? data?.error ?? null,
      };
    } catch {
      return { ok: false, msg: null };
    }
  };

  const tryText = async (): Promise<string | null> => {
    if (typeof resp.text !== 'function') return null;

    try {
      const text = await resp.text();
      return text || null;
    } catch {
      return null;
    }
  };

  const contentType =
    resp.headers?.get?.('content-type')?.toLowerCase() ?? '';
  const hasHeaders = typeof resp.headers?.get === 'function';

  // Known JSON response → parse JSON only
  if (contentType.includes('application/json')) {
    const { msg } = await tryJson();
    return msg;
  }

  // Headers missing (likely mocks) → choose a single read path
  if (!hasHeaders && typeof resp.json === 'function') {
    const { ok, msg } = await tryJson();
    return ok ? msg : await tryText();
  }

  // Default: treat as text
  return await tryText();
}
